import { useRef, useState } from "react";

import { Pagination, Stack, Typography } from "@mui/material";

import { CompanyReview } from "components/CompanyReview";
import { ErrorTypography } from "components/ErrorTypography";
import { LoadingWrappedNode } from "components/LoadingWrappedNode";

import { getReviewAuthor, getReviewCompany } from "./utils";

import type { CompanyReviewProfile } from "types/company";

interface Props {
  loading: boolean;
  error: string | null;
  reviewProfile: CompanyReviewProfile | null;
}

const REVIEWS_PER_PAGE = 5;

export function CompanyPrivilegedReviews(props: Props) {
  const { loading, error, reviewProfile } = props;
  const [currentPage, setCurrentPage] = useState(1);
  const reviewListRef = useRef<HTMLDivElement>(null);

  const totalReviews = reviewProfile?.reviews.length ?? 0;
  const totalPages = Math.ceil(totalReviews / REVIEWS_PER_PAGE);

  const startIndex = (currentPage - 1) * REVIEWS_PER_PAGE;
  const endIndex = startIndex + REVIEWS_PER_PAGE;
  const paginatedReviews =
    reviewProfile?.reviews.slice(startIndex, endIndex) ?? [];

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    page: number,
  ) => {
    setCurrentPage(page);
    // Back to the first review of the new page, not the top of the profile:
    // the reviews sit near the bottom of a long page.
    reviewListRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  if (error) {
    return <ErrorTypography error={error} />;
  }

  return (
    <LoadingWrappedNode loading={loading}>
      {totalReviews === 0 ? (
        <Stack alignItems="center">
          <Typography variant="body1" color="text.secondary">
            No reviews created yet.
          </Typography>
        </Stack>
      ) : (
        <Stack spacing={2} ref={reviewListRef} sx={{ scrollMarginTop: 96 }}>
          {paginatedReviews.map((review) => (
            <CompanyReview
              key={review.created_at}
              text={review.text}
              author={getReviewAuthor(review)}
              createdAt={review.created_at}
              rating={review.rating}
              authorCompany={getReviewCompany(review)}
            />
          ))}
          {totalPages > 1 && (
            <Stack alignItems="center" sx={{ mt: 3 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
              />
            </Stack>
          )}
        </Stack>
      )}
    </LoadingWrappedNode>
  );
}
